import axios, { AxiosRequestConfig } from 'axios';
import { API_URL, YJ_ACCESS_TOKEN } from '../configuration';
import { forceLogout } from '../utils/authManager';

const apiOrigin = (() => {
  try {
    return new URL(API_URL).origin;
  } catch {
    return API_URL;
  }
})();

const isApiRequest = (config?: AxiosRequestConfig): boolean => {
  if (!config?.url) {
    return false;
  }

  try {
    const requestUrl = new URL(config.url, config.baseURL || API_URL);
    return requestUrl.origin === apiOrigin;
  } catch {
    return typeof config.url === 'string' && config.url.includes(apiOrigin);
  }
};

let interceptorsConfigured = false;

const configureInterceptors = (): void => {
  if (interceptorsConfigured) {
    return;
  }

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;

      if (isApiRequest(error.config) && (status === 401 || status === 403)) {
        forceLogout({
          reason: status === 401 ? 'TOKEN_EXPIRED' : 'UNAUTHORIZED',
          message: status === 401 ? '\u767b\u5165\u5df2\u5931\u6548\uff0c\u8acb\u91cd\u65b0\u767b\u5165\u3002' : '\u7121\u6cd5\u5b58\u53d6\u6b64\u529f\u80fd\uff0c\u8acb\u91cd\u65b0\u767b\u5165\u3002',
        });
      }

      if (typeof error.response?.data === 'object') {
        error.response.data = JSON.stringify(error.response.data);
      }

      return Promise.reject(error);
    },
  );

  interceptorsConfigured = true;
};

configureInterceptors();

export const getAuthHeader = () => {
  const yjToken = localStorage.getItem(YJ_ACCESS_TOKEN);

  if (!yjToken) {
    forceLogout({ reason: 'TOKEN_MISSING' });
    return undefined;
  }

  return { headers: { Authorization: `Bearer ${yjToken}`, 'Content-Type': 'application/json' } };
};

export const getLineAuthHeader = (idToken: string) => {
  return { headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' } };
};

export async function get(url: string, config?: AxiosRequestConfig) {
  return axios.get(`${url}`, { ...config });
}

export async function post(url: string, payload?: object, config?: AxiosRequestConfig) {
  return axios.post(`${url}`, payload, { responseType: 'json', ...config });
}

export async function patch(url: string, payload: object) {
  return axios.patch(`${url}`, payload, { responseType: 'json' }).then((response) => response);
}

export async function _delete(url: string) {
  return axios.delete(`${url}`).then((response) => response);
}
